'use client'

import * as React from 'react'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * The one confirmation dialog for destructive or costly actions.
 *
 * Replaces `window.confirm(...)` and one-click destructive buttons. Built on
 * `ui/alert-dialog` (Radix), so focus is trapped and Escape cancels.
 *
 * - `destructive` renders the confirm button in danger red.
 * - `confirmPhrase` requires the user to type an exact phrase before the
 *   confirm button enables (for irreversible actions).
 * - `onConfirm` may be async: while it runs, both buttons are disabled and the
 *   dialog cannot be dismissed. It closes when `onConfirm` resolves; if it
 *   throws, the dialog stays open so the caller's error toast/inline message is
 *   read in context and the user can retry or cancel.
 */
export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  /** Extra body content rendered under the description (lists, counts, warnings). */
  children?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Label shown on the confirm button while `onConfirm` is pending. */
  pendingLabel?: string
  destructive?: boolean
  /** When set, the confirm button stays disabled until this exact text is typed. */
  confirmPhrase?: string
  /** Externally-owned pending state (e.g. a parent `useTransition`). */
  pending?: boolean
  onConfirm: () => void | Promise<void>
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  pendingLabel,
  destructive = false,
  confirmPhrase,
  pending: externalPending = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [internalPending, setInternalPending] = React.useState(false)
  const [typed, setTyped] = React.useState('')
  const inputId = React.useId()
  const pending = internalPending || externalPending

  // Reset the typed phrase whenever the dialog is (re)opened.
  React.useEffect(() => {
    if (open) setTyped('')
  }, [open])

  const phraseSatisfied = !confirmPhrase || typed.trim() === confirmPhrase
  const canConfirm = phraseSatisfied && !pending

  const handleOpenChange = (next: boolean) => {
    if (pending && !next) return
    onOpenChange(next)
  }

  const handleConfirm = async () => {
    if (!canConfirm) return
    setInternalPending(true)
    try {
      await onConfirm()
      onOpenChange(false)
    } catch {
      // Caller owns error reporting; keep the dialog open for retry/cancel.
    } finally {
      setInternalPending(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description != null && (
            <AlertDialogDescription className="whitespace-pre-line">
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {children}
        {confirmPhrase && (
          <div className="space-y-1.5">
            <label htmlFor={inputId} className="text-sm text-ink-70">
              Type <span className="font-mono text-ink-100">{confirmPhrase}</span> to confirm.
            </label>
            <Input
              id={inputId}
              value={typed}
              autoComplete="off"
              spellCheck={false}
              disabled={pending}
              onChange={(event) => setTyped(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && canConfirm) {
                  event.preventDefault()
                  void handleConfirm()
                }
              }}
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'primary'}
            disabled={!canConfirm}
            aria-busy={pending || undefined}
            onClick={() => void handleConfirm()}
          >
            {pending ? (pendingLabel ?? `${confirmLabel}…`) : confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
